<style lang="scss">
@import 'resources/sass/v1/abstracts/_variables';

.articles-overview {
    position: relative;

    .filter-hero {
        position: relative;

        @media screen and (min-width: 1441px) {
            min-height: 424px;
        }

        p {
            line-height: 2;
        }

        .input-search-wrapper {
            position: relative;

            &:before {
                display: block;
                content: '';
                background-image: url('/svg/icons/icon-search-gray.svg');
                background-size: cover;
                background-repeat: no-repeat;
                position: absolute;
                z-index: 50;
                width: 12px;
                height: 12px;
                left: 40px;
                top: 50%;
                transform: translateY(-50%);
            }
        }

        input {
            position: relative;
            border-radius: 2px;
            padding-left: 72px;

            &::placeholder {
                color: $gray;
            }

            @media screen and (min-width: $screen-lg) {
                border-radius: 2px 0 0 2px;
            }
        }

        input,
        button {
            @media screen and (min-width: $screen-lg) {
                min-height: 72px;
            }
        }
    }

    .main-filter {
        .btn {
            padding-left: 64px;
            padding-right: 64px;
        }
    }

    .articles-body {
        .articles {
            .fade-enter-active,
            .fade-leave-active {
                transition: opacity 0.3s;
            }
            .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
                opacity: 0;
            }
        }
    }
}
</style>

<template>
    <div class="articles-overview mb-20 md:mb-[7.5rem] lg:mb-40">
        <div class="filter-hero bg-primary pt-10 md:pt-14 lg:pt-20 xl:pt-[10rem] pb-8 md:pb-6">
            <div class="container">
                <div class="intro flex flex-wrap md:-m-7 xl:-m-[3.125rem]">
                    <div class="mt-10 flex w-full flex-col md:mt-0 md:w-6/12 md:p-7 xl:p-[3.125rem]">
                        <h1 class="text-44 font-bold text-white">{{ translations.page_title }}</h1>
                    </div>
                    <div class="mt-5 flex w-full flex-col md:mt-0 md:w-6/12 md:p-7 xl:p-[3.125rem]">
                        <div class="prose prose-lg text-white" v-html="translations.content"></div>
                    </div>
                </div>

                <div class="main-filter lg:flex mt-10 lg:mt-20">
                    <div class="w-full mb-4 lg:mb-0">
                        <div class="input-search-wrapper">
                            <input
                                type="text"
                                class="w-full min-h-[3.5rem] focus:ring-0 focus:outline-none"
                                @keyup.enter="applySearchField"
                                v-model="searchInput"
                                :placeholder="globalTranslations.kb_search_placeholder"
                            />
                        </div>
                    </div>
                    <div class="bg-white">
                        <button class="bg-green hover:bg-purple min-h-[3.5rem] transition-colors text-white w-full lg:w-auto px-20" @click="applySearchField">
                            <span>{{ globalTranslations.search }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-white w-screen mb-14 md:mb-[7.5rem]">
            <svg-vue icon="bottom-wave-4" class="text-primary h-auto w-full"></svg-vue>
        </div>

        <div class="articles-body container">
            <transition name="fade">
                <div v-if="filteredarticles.length === 0" class="text-center">
                    <span class="h2 block py-20" v-if="ready">{{ globalTranslations.no_search_results }}</span>
                    <div class="spinner-background flex justify-center items-center" v-else>
                        <div class="spinner"></div>
                    </div>
                </div>
            </transition>

            <transition name="fade">
                <div v-if="filteredCategories.length > 0" class="flex justify-center">
                    <div class="w-full md:w-8/12">
                        <div class="secondary-filter md:text-center mb-10 md:mb-20">
                            <div class="tags -mb-2">
                                <span
                                    v-for="category in filteredCategories"
                                    :key="`filter.${category.id}`"
                                    :class="{ active: category.id == filter.category }"
                                    :data-id="category.id"
                                    class="tag mb-2"
                                    @click="toggleCategory(category.id)"
                                    >{{ category.name }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </transition>

            <div id="articles" ref="articles" class="articles" v-if="ready">
                <transition-group name="fade" class="flex flex-wrap -m-[.625rem] md:-m-5" tag="div">
                    <div v-for="article in filteredarticles" :key="article.id" class="p-[.625rem] md:p-5 w-full md:w-1/2 lg:w-1/3">
                        <article-card :article-item="article" :translations="translations" :categories="getArticleCategories(article['kb-category'])" :locale="locale"></article-card>
                    </div>
                </transition-group>
            </div>
        </div>
    </div>
</template>

<script>
var scrollIntoView = require('scroll-into-view');

export default {
    props: {
        translations: {
            type: Object,
            required: true,
        },
        globalTranslations: {
            type: Object,
            required: true,
        },
        locale: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            ready: false,
            articles: [],
            categories: [],
            filteredCategories: [], // category tags filtered by visible articles

            searchInput: null,
            filter: {
                keyword: null,
                category: null,
            },           
        };
    },

    mounted() {
        var self = this;

        var promises = [this.getArticles(), this.getCategories()];

        Promise.all(promises).then(
            function () {
                const setFilterByParameter = () => {
                    const params = new URLSearchParams(location.search);

                    var category = params.get('category');
                    var keyword = params.get('keyword');

                    self.filter.keyword = keyword;
                    self.searchInput = keyword;

                    if (category) {
                        self.filter.category = self.getCategoryBySlug(category).id;
                    }
                };

                setFilterByParameter();

                self.calculateCategories();

                self.ready = true;
            },
            function (error) {
                console.log(error);
            },
        );
    },

    methods: {
        getArticles() {
            const self = this;
            return new Promise(function (resolve, reject) {
                axios
                    .get(`/api/articles?lang=${document.documentElement.lang}`)
                    .then(function (response) {
                        self.articles = response.data;
                        resolve(response.data);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },

        getCategories() {
            const self = this;
            return new Promise(function (resolve, reject) {
                axios
                    .get(`/api/articlecategories?lang=${document.documentElement.lang}`)
                    .then(function (response) {
                        self.categories = response.data;
                        resolve(response.data);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },

        populateSearchParams() {
            var self = this;
            const params = new URLSearchParams();
            for (let param in this.filter) {
                if (this.filter[param] !== null) {
                    if (Array.isArray(this.filter[param]) && this.filter[param].length) {
                        this.filter[param].forEach((ai) => {
                            params.append(`${param}[]`, ai);
                        });
                    }
                    if (!Array.isArray(this.filter[param])) {
                        if (param == 'category') {
                            params.set(param, self.getCategoryById(this.filter[param]).slug);
                        } else {
                            params.set(param, this.filter[param]);
                        }
                    }
                }
            }

            let newUrl = location.pathname;
            if (params.toString()) {
                newUrl += `?${params}`;
            }

            window.history.replaceState({}, '', newUrl);
        },

        getCategoryById(id) {
            var match = this.categories.filter((cat) => cat.id === id);
            if (match.length > 0) {
                return match[0];
            }
            return null;
        },

        getCategoryBySlug(slug) {
            var match = this.categories.filter((cat) => cat.slug === slug);
            if (match.length > 0) {
                return match[0];
            }
            return null;
        },

        applySearchField() {
            this.filter.keyword = this.searchInput;

            this.filter.category = null;
            this.calculateCategories();

            this.populateSearchParams();

            scrollIntoView(this.$refs.articles);
        },

        toggleCategory(id) {
            if (this.filter.category == id) {
                this.filter.category = null;
            } else {
                this.filter.category = id;
            }

            this.populateSearchParams();

            scrollIntoView(this.$refs.articles);
        },

        getArticleCategories(articleCategories) {
            return this.categories.filter((category) => {
                return articleCategories.includes(category.id);
            });
        },

        calculateCategories() {
            let self = this;
            var filteredCategories = [];

            for (var i = 0; i < self.filteredarticles.length; i++) {
                for (var j = 0; j < self.filteredarticles[i]['kb-category'].length; j++) {
                    var appliccablecategory = self.filteredarticles[i]['kb-category'][j];
                    if (
                        appliccablecategory != null &&
                        filteredCategories.filter((category) => {
                            return category.id === appliccablecategory;
                        }).length == 0
                    ) {
                        var categoryObject = self.categories.filter((category) => {
                            return category.id === appliccablecategory;
                        });
                        filteredCategories.push(categoryObject[0]);
                    }
                }
            }

            this.filteredCategories = filteredCategories;
        },
    },

    computed: {
        filteredarticles() {
            let self = this;
            var filteredArticles = this.articles;

            if (self.filter.category != null) {
                filteredArticles = filteredArticles.filter((article) => article['kb-category'].includes(self.filter.category));
            }

            if (self.filter.keyword != null && self.filter.keyword != '') {
                filteredArticles = filteredArticles.filter(
                    (article) =>
                        article.title.rendered.toLowerCase().indexOf(self.filter.keyword.toLowerCase()) >= 0 || article.acf.content.toLowerCase().indexOf(self.filter.keyword.toLowerCase()) >= 0,
                );
            }

            return filteredArticles;
        },
    },
};
</script>
